import React, {useMemo} from 'react';

import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import isConfiguration from 'notebook/util/isConfiguration';
import {WorksheetContext} from 'notebook/worksheets/WorksheetContext';

/**
 * Returns the fileId to use when opening a blob preview tab.
 *
 * Step documents inside worksheets and procedure configurations have no FileState of their
 * own. We prefer the containing worksheet or procedure ID, both of which have FileStates with
 * tab buffers. This mirrors NoteLinkedFilePreview.getContainerTabId() / previewFileId logic.
 */
export function useBlobLinkPreviewFileId(): string | undefined {
  const {fileId, procedureId} = EditorPropertiesContext.useContext();
  const {worksheet} = WorksheetContext.useContext();
  return worksheet?.id ?? procedureId ?? fileId;
}

interface BlobLinkPreviewFileIdConsumerProps {
  children: (previewFileId: string | undefined) => JSX.Element | null;
}

export function BlobLinkPreviewFileIdConsumer(props: BlobLinkPreviewFileIdConsumerProps): JSX.Element | null {
  const previewFileId = useBlobLinkPreviewFileId();
  return props.children(previewFileId);
}

interface BlobLinkPreviewTabProviderProps {
  fileId: string;
  children: JSX.Element;
}

/**
 * Provides the necessary props for BlobLinkList to open a preview in a new tab
 *
 * This wraps the notebook EditorPropertiesContext since a lot of these tag rendering components are coupled
 * with notebook.
 *
 * TODO(BNCH-20316): Refactor out the notebook dependency
 */
export function BlobLinkPreviewTabProvider(props: BlobLinkPreviewTabProviderProps): JSX.Element {
  const editorPropertiesContextValue = useMemo(
    () => ({
      fileId: props.fileId,
      showFormulas: false,
      isConfig: isConfiguration(props.fileId),
    }),
    [props.fileId]
  );
  return (
    <EditorPropertiesContext.Provider value={editorPropertiesContextValue}>
      {props.children}
    </EditorPropertiesContext.Provider>
  );
}
